import { createEffect } from '@databases/selections';
import { type SelectionsApiContract } from '@databases/selections-api-types';

interface CellPosition {
  row: { index: number };
  col: { index: number };
}

export interface CellEditingEffectDeps {
  selectionsApi: SelectionsApiContract;
  /**
   * Called when editing starts or stops on a cell.
   * `context` indicates where the edit is happening — 'table' or 'panel'.
   * On stop, `context` reflects where the edit was opened (used to close the right editor).
   */
  notifyCellEditing: (params: {
    entryId: string;
    fieldId: string;
    editing: boolean;
    context: 'table' | 'panel';
    initialKey?: string;
  }) => void;
}

export const createCellEditingEffect = createEffect(
  ({ selectionsApi, notifyCellEditing }: CellEditingEffectDeps) => {
    let isOpen = false;
    let openPosition: CellPosition | undefined;
    let openContext: 'table' | 'panel' = 'table';

    const getCellIds = (position: CellPosition) =>
      selectionsApi.resolvePositionToIds(position, 'cell');

    const setCellEditingState = (params: {
      position: CellPosition;
      editing: boolean;
      context: 'table' | 'panel';
      initialKey?: string;
    }) => {
      const { entryId, fieldId } = getCellIds(params.position);

      if (entryId && fieldId) {
        notifyCellEditing({
          entryId,
          fieldId,
          editing: params.editing,
          context: params.context,
          initialKey: params.initialKey,
        });
      }
    };

    return {
      dispose() {
        if (isOpen && openPosition) {
          setCellEditingState({
            position: openPosition,
            editing: false,
            context: openContext,
          });
        }

        isOpen = false;
        openPosition = undefined;
      },

      deltaTypes: ['cellEditRequested', 'cellEditStopped'],

      onDelta(delta) {
        if (delta.kind === 'cellEditRequested') {
          const { entryId, fieldId } = getCellIds(delta.position);

          if (!fieldId || !entryId) {
            return;
          }

          const context = delta.editingContext;

          setCellEditingState({
            position: delta.position,
            editing: true,
            context,
            initialKey: delta.initialKey,
          });

          isOpen = true;
          openPosition = delta.position;
          openContext = context;
        } else if (delta.kind === 'cellEditStopped') {
          if (isOpen && openPosition) {
            setCellEditingState({
              position: openPosition,
              editing: false,
              context: openContext,
            });
          }

          isOpen = false;
          openPosition = undefined;
        }
      },
    };
  }
);
